import { supabase } from './supabase';
import { buildPublicFeedbackUrl, getFeedbackPublicOriginFromBusinessRow } from './feedbackPublicUrl';
import { getSiteUrl } from './siteConfig';

export interface BookingFeedback {
  id: string;
  booking_id: string;
  business_id: string;
  token: string;
  customer_name?: string;
  customer_email?: string;
  overall_quality_rating: number;
  overall_experience_rating: number;
  improvement_suggestions?: string;
  submitted_at: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'submitted' | 'expired';
  form_responses?: { [fieldKey: string]: string | number };
  // Joined data
  booking?: {
    id: string;
    booking_number: string;
    title: string;
    start_time: string;
    end_time: string;
    customer?: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
    };
    resources?: Array<{
      resource: {
        id: string;
        first_name: string;
        last_name: string;
      };
    }>;
  };
}

export interface FeedbackSubmission {
  overall_quality_rating: number;
  overall_experience_rating: number;
  improvement_suggestions?: string;
}

/**
 * Generate a secure, unique feedback token
 */
export function generateFeedbackToken(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  const extraRandom = Math.random().toString(36).substring(2, 15);
  return `fb_${timestamp}_${randomPart}_${extraRandom}`;
}

/**
 * Check if feedback is enabled for a business
 */
export async function isFeedbackEnabled(businessId: string): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('businesses')
      .select('feedback_enabled')
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error checking feedback enabled status:', error);
      return false;
    }

    // Default to true if not set
    return data?.feedback_enabled !== false;
  } catch (error) {
    console.error('Error checking feedback enabled status:', error);
    return false;
  }
}

/**
 * Generate feedback link for a completed booking
 */
export async function generateFeedbackForBooking(bookingId: string): Promise<string | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // First, get the booking details
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select(`
        id,
        business_id,
        title,
        booking_number,
        start_time,
        customer:customers!bookings_customer_id_fkey(first_name, last_name, email, phone)
      `)
      .eq('id', bookingId)
      .single();

    if (bookingError || !booking) {
      console.error('Error fetching booking:', bookingError);
      return null;
    }

    // Check if feedback is enabled for this business
    const feedbackIsEnabled = await isFeedbackEnabled(booking.business_id);
    if (!feedbackIsEnabled) {
      console.log(`Feedback is disabled for business ${booking.business_id}, skipping feedback generation`);
      return null;
    }

    // Check if feedback already exists for this booking
    const { data: existingFeedback } = await supabase
      .from('booking_feedback')
      .select('token')
      .eq('booking_id', bookingId)
      .single();

    if (existingFeedback) {
      // Return existing token
      return existingFeedback.token;
    }

    // Generate new feedback token
    const token = generateFeedbackToken();

    // Create feedback record
    const { data: feedback, error: feedbackError } = await supabase
      .from('booking_feedback')
      .insert({
        booking_id: bookingId,
        business_id: booking.business_id,
        token: token,
        customer_name: booking.customer ? `${booking.customer.first_name} ${booking.customer.last_name}` : null,
        customer_email: booking.customer?.email || null,
        status: 'pending'
      })
      .select()
      .single();

    if (feedbackError) {
      console.error('Error creating feedback record:', feedbackError);
      return null;
    }

    // Update booking with feedback token for easy reference
    await supabase
      .from('bookings')
      .update({ feedback_token: token })
      .eq('id', bookingId);

    return token;
  } catch (error) {
    console.error('Error generating feedback:', error);
    return null;
  }
}

/**
 * Get feedback details by token (for public feedback page)
 */
export async function getFeedbackByToken(token: string): Promise<BookingFeedback | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    const { data: feedback, error } = await supabase
      .from('booking_feedback')
      .select(`
        *,
        booking:bookings(
          id,
          booking_number,
          title,
          start_time,
          end_time,
          customer:customers!bookings_customer_id_fkey(first_name, last_name, email, phone),
          resources:booking_resources(
            resource:resources(id, first_name, last_name)
          )
        )
      `)
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error || !feedback) {
      return null;
    }

    return feedback;
  } catch (error) {
    console.error('Error fetching feedback by token:', error);
    return null;
  }
}

/**
 * Submit feedback for a booking
 */
export async function submitFeedback(
  token: string, 
  submission: FeedbackSubmission
): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    // First, get the booking_id from the feedback record
    const { data: existingFeedback, error: fetchError } = await supabase
      .from('booking_feedback')
      .select('booking_id')
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .single();

    if (fetchError || !existingFeedback) {
      console.error('Error fetching feedback record:', fetchError);
      return false;
    }

    const { error } = await supabase
      .from('booking_feedback')
      .update({
        overall_quality_rating: submission.overall_quality_rating,
        overall_experience_rating: submission.overall_experience_rating,
        improvement_suggestions: submission.improvement_suggestions || null,
        status: 'submitted',
        submitted_at: new Date().toISOString()
      })
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString());

    if (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }

    // Trigger feedback automations after successful submission
    try {
      // Fetch the updated feedback with booking data
      const updatedFeedback = await getBookingFeedback(existingFeedback.booking_id);
      if (updatedFeedback) {
        // Dynamically import to avoid circular dependencies
        const { triggerFeedbackAutomations } = await import('./automationsV2');
        await triggerFeedbackAutomations(updatedFeedback);
      }
    } catch (automationError) {
      console.error('Error triggering feedback automations:', automationError);
      // Don't fail the submission if automation trigger fails
    }

    return true;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return false;
  }
}

/**
 * Get submitted feedback for a specific booking (for admin view)
 */
export async function getBookingFeedback(bookingId: string): Promise<BookingFeedback | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // Remove .single() to avoid 406 errors when no feedback exists
    const { data: feedbackArray, error } = await supabase
      .from('booking_feedback')
      .select(`
        *,
        booking:bookings(
          id,
          booking_number,
          title,
          start_time,
          end_time,
          customer:customers!bookings_customer_id_fkey(first_name, last_name, email, phone)
        )
      `)
      .eq('booking_id', bookingId)
      .eq('status', 'submitted')
      .limit(1);

    if (error) {
      console.error('Error fetching booking feedback:', error);
      return null;
    }

    // Return the first item if it exists, otherwise null
    return feedbackArray && feedbackArray.length > 0 ? feedbackArray[0] : null;
  } catch (error) {
    console.error('Error fetching booking feedback:', error);
    return null;
  }
}

/**
 * Get all feedback for a business (admin view)
 */
export async function getBusinessFeedback(businessId: string): Promise<BookingFeedback[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    const { data: feedback, error } = await supabase
      .from('booking_feedback')
      .select(`
        *,
        booking:bookings(
          id,
          booking_number,
          title,
          start_time,
          end_time,
          customer:customers!bookings_customer_id_fkey(first_name, last_name, email, phone)
        )
      `)
      .eq('business_id', businessId)
      .eq('status', 'submitted')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching business feedback:', error);
      return [];
    }

    return feedback || [];
  } catch (error) {
    console.error('Error fetching business feedback:', error);
    return [];
  }
}

/**
 * Get resource performance based on feedback
 */
export async function getResourcePerformance(businessId: string): Promise<Array<{
  resource_id: string;
  resource_name: string;
  average_quality: number;
  average_experience: number;
  total_ratings: number;
}>> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    // This is a complex query that joins feedback with booking resources
    const { data, error } = await supabase
      .rpc('get_resource_feedback_performance', { 
        target_business_id: businessId 
      });

    if (error) {
      console.error('Error fetching resource performance:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching resource performance:', error);
    return [];
  }
}

/**
 * Generate feedback link URL
 */
export function generateFeedbackURL(token: string, baseURL?: string): string {
  const base = baseURL ?? (typeof window !== 'undefined' ? window.location.origin : getSiteUrl());
  return `${base}/feedback/${token}`;
}

/** Respects verified feedback custom hostname when present. */
export async function buildFeedbackCustomerLink(token: string, businessId: string): Promise<string> {
  if (!token) return '';
  if (!supabase) {
    return generateFeedbackURL(token);
  }
  try {
    const { data, error } = await supabase
      .from('businesses')
      .select('feedback_custom_hostname, feedback_custom_host_verified_at')
      .eq('id', businessId)
      .maybeSingle();
    if (error) {
      console.error('buildFeedbackCustomerLink: business fetch failed', error);
      return generateFeedbackURL(token);
    }
    const origin = getFeedbackPublicOriginFromBusinessRow(data);
    return buildPublicFeedbackUrl(token, origin);
  } catch (e) {
    console.error('buildFeedbackCustomerLink', e);
    return generateFeedbackURL(token);
  }
}

export interface ResourceFeedbackData {
  resource_id: string;
  resource_name: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  average_quality_rating: number;
  average_experience_rating: number;
  overall_average_rating: number;
  total_feedback_count: number;
}

export interface BusinessFeedbackReport {
  overall_business_rating: number;
  total_feedback_count: number;
  resources: ResourceFeedbackData[];
}

/**
 * Get comprehensive feedback report for a business
 */
export async function getBusinessFeedbackReport(businessId: string): Promise<BusinessFeedbackReport | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // Get all feedback for this business with booking and resource information
    const { data: feedbackData, error: feedbackError } = await supabase
      .from('booking_feedback')
      .select(`
        *,
        bookings!inner(
          id,
          business_id,
          booking_resources!inner(
            resource_id,
            resources!inner(
              id,
              first_name,
              last_name,
              avatar_url
            )
          )
        )
      `)
      .eq('business_id', businessId)
      .eq('status', 'submitted')
      .not('overall_quality_rating', 'is', null)
      .not('overall_experience_rating', 'is', null);

    if (feedbackError) {
      console.error('Error fetching feedback data:', feedbackError);
      return null;
    }

    if (!feedbackData || feedbackData.length === 0) {
      return {
        overall_business_rating: 0,
        total_feedback_count: 0,
        resources: []
      };
    }

    // Calculate overall business rating
    const totalQualityRating = feedbackData.reduce((sum, feedback) => sum + (feedback.overall_quality_rating || 0), 0);
    const totalExperienceRating = feedbackData.reduce((sum, feedback) => sum + (feedback.overall_experience_rating || 0), 0);
    const overallBusinessRating = ((totalQualityRating + totalExperienceRating) / 2) / feedbackData.length;

    // Group feedback by resources
    const resourceFeedbackMap = new Map<string, {
      resource_id: string;
      resource_name: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
      qualityRatings: number[];
      experienceRatings: number[];
    }>();

    feedbackData.forEach(feedback => {
      if (feedback.bookings?.booking_resources) {
        feedback.bookings.booking_resources.forEach((bookingResource: any) => {
          if (bookingResource.resources) {
            const resource = bookingResource.resources;
            const resourceId = resource.id;
            
            if (!resourceFeedbackMap.has(resourceId)) {
              resourceFeedbackMap.set(resourceId, {
                resource_id: resourceId,
                resource_name: `${resource.first_name} ${resource.last_name}`,
                first_name: resource.first_name,
                last_name: resource.last_name,
                avatar_url: resource.avatar_url,
                qualityRatings: [],
                experienceRatings: []
              });
            }

            const resourceData = resourceFeedbackMap.get(resourceId)!;
            resourceData.qualityRatings.push(feedback.overall_quality_rating || 0);
            resourceData.experienceRatings.push(feedback.overall_experience_rating || 0);
          }
        });
      }
    });

    // Calculate resource averages
    const resources: ResourceFeedbackData[] = Array.from(resourceFeedbackMap.values()).map(resourceData => {
      const avgQuality = resourceData.qualityRatings.reduce((sum, rating) => sum + rating, 0) / resourceData.qualityRatings.length;
      const avgExperience = resourceData.experienceRatings.reduce((sum, rating) => sum + rating, 0) / resourceData.experienceRatings.length;
      const overallAverage = (avgQuality + avgExperience) / 2;

      return {
        resource_id: resourceData.resource_id,
        resource_name: resourceData.resource_name,
        first_name: resourceData.first_name,
        last_name: resourceData.last_name,
        avatar_url: resourceData.avatar_url,
        average_quality_rating: Number(avgQuality.toFixed(1)),
        average_experience_rating: Number(avgExperience.toFixed(1)),
        overall_average_rating: Number(overallAverage.toFixed(1)),
        total_feedback_count: resourceData.qualityRatings.length
      };
    });

    // Sort resources by overall average rating (highest first)
    resources.sort((a, b) => b.overall_average_rating - a.overall_average_rating);

    return {
      overall_business_rating: Number(overallBusinessRating.toFixed(1)),
      total_feedback_count: feedbackData.length,
      resources
    };

  } catch (error) {
    console.error('Error generating business feedback report:', error);
    return null;
  }
}

/**
 * Delete feedback for a booking when status changes from completed
 */
export async function deleteFeedbackForBooking(bookingId: string): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    console.log(`[FEEDBACK] Deleting feedback for booking: ${bookingId}`);
    
    // Delete the feedback record (soft delete by setting is_active to false)
    const { error } = await supabase
      .from('booking_feedback')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('booking_id', bookingId)
      .eq('is_active', true);

    if (error) {
      console.error('Error deleting feedback for booking:', error);
      return false;
    }

    // Also clear the feedback_token from the booking
    const { error: bookingError } = await supabase
      .from('bookings')
      .update({ 
        feedback_token: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', bookingId);

    if (bookingError) {
      console.error('Error clearing feedback token from booking:', error);
      return false;
    }

    console.log(`[FEEDBACK] ✅ Successfully deleted feedback for booking: ${bookingId}`);
    return true;
  } catch (error) {
    console.error('Error deleting feedback for booking:', error);
    return false;
  }
}

/**
 * Get comprehensive feedback report for a business using dynamic form fields
 */
export async function getDynamicBusinessFeedbackReport(
  businessId: string, 
  dateRange?: { startDate?: string; endDate?: string }
): Promise<BusinessFeedbackReport | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // First, get the feedback form fields for this business
    const { data: formFields, error: fieldsError } = await supabase
      .from('feedback_form_fields')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .eq('field_type', 'rating')
      .order('display_order', { ascending: true });

    if (fieldsError) {
      console.error('Error fetching form fields:', fieldsError);
      return null;
    }

    // Build the query with optional date filtering
    let query = supabase
      .from('booking_feedback')
      .select(`
        *,
        bookings!inner(
          id,
          business_id,
          booking_resources!inner(
            resource_id,
            resources!inner(
              id,
              first_name,
              last_name,
              avatar_url
            )
          )
        )
      `)
      .eq('business_id', businessId)
      .eq('status', 'submitted');

    // Apply date filtering if provided
    if (dateRange?.startDate) {
      query = query.gte('submitted_at', dateRange.startDate);
    }
    if (dateRange?.endDate) {
      query = query.lte('submitted_at', dateRange.endDate);
    }

    const { data: feedbackData, error: feedbackError } = await query;

    if (feedbackError) {
      console.error('Error fetching feedback data:', feedbackError);
      return null;
    }

    if (!feedbackData || feedbackData.length === 0) {
      return {
        overall_business_rating: 0,
        total_feedback_count: 0,
        resources: []
      };
    }

    // Filter feedback that has either form_responses or legacy ratings
    const validFeedback = feedbackData.filter(feedback => {
      if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
        // Has dynamic responses - check if at least one rating field has a value
        return formFields?.some(field => {
          const value = feedback.form_responses![field.field_key];
          return typeof value === 'number' && value > 0;
        });
      } else {
        // Fallback to legacy fields
        return feedback.overall_quality_rating || feedback.overall_experience_rating;
      }
    });

    if (validFeedback.length === 0) {
      return {
        overall_business_rating: 0,
        total_feedback_count: 0,
        resources: []
      };
    }

    // Calculate overall business rating using dynamic fields
    let totalBusinessRating = 0;
    let totalBusinessRatingCount = 0;

    validFeedback.forEach(feedback => {
      if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
        // Use dynamic responses
        let feedbackTotal = 0;
        let feedbackCount = 0;
        
        formFields?.forEach(field => {
          const value = feedback.form_responses![field.field_key];
          if (typeof value === 'number' && value > 0) {
            feedbackTotal += value;
            feedbackCount++;
          }
        });
        
        if (feedbackCount > 0) {
          totalBusinessRating += feedbackTotal / feedbackCount;
          totalBusinessRatingCount++;
        }
      } else {
        // Fallback to legacy fields
        let legacyTotal = 0;
        let legacyCount = 0;
        
        if (feedback.overall_quality_rating) {
          legacyTotal += feedback.overall_quality_rating;
          legacyCount++;
        }
        if (feedback.overall_experience_rating) {
          legacyTotal += feedback.overall_experience_rating;
          legacyCount++;
        }
        
        if (legacyCount > 0) {
          totalBusinessRating += legacyTotal / legacyCount;
          totalBusinessRatingCount++;
        }
      }
    });

    const overallBusinessRating = totalBusinessRatingCount > 0 ? totalBusinessRating / totalBusinessRatingCount : 0;

    // Group feedback by resources using dynamic fields
    const resourceFeedbackMap = new Map<string, {
      resource_id: string;
      resource_name: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
      ratings: number[];
    }>();

    validFeedback.forEach(feedback => {
      if (feedback.bookings?.booking_resources) {
        // Calculate average rating for this feedback
        let feedbackAverage = 0;
        
        if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
          // Use dynamic responses
          let feedbackTotal = 0;
          let feedbackCount = 0;
          
          formFields?.forEach(field => {
            const value = feedback.form_responses![field.field_key];
            if (typeof value === 'number' && value > 0) {
              feedbackTotal += value;
              feedbackCount++;
            }
          });
          
          feedbackAverage = feedbackCount > 0 ? feedbackTotal / feedbackCount : 0;
        } else {
          // Fallback to legacy fields
          let legacyTotal = 0;
          let legacyCount = 0;
          
          if (feedback.overall_quality_rating) {
            legacyTotal += feedback.overall_quality_rating;
            legacyCount++;
          }
          if (feedback.overall_experience_rating) {
            legacyTotal += feedback.overall_experience_rating;
            legacyCount++;
          }
          
          feedbackAverage = legacyCount > 0 ? legacyTotal / legacyCount : 0;
        }

        if (feedbackAverage > 0) {
          feedback.bookings.booking_resources.forEach((bookingResource: any) => {
            if (bookingResource.resources) {
              const resource = bookingResource.resources;
              const resourceId = resource.id;
              
              if (!resourceFeedbackMap.has(resourceId)) {
                resourceFeedbackMap.set(resourceId, {
                  resource_id: resourceId,
                  resource_name: `${resource.first_name} ${resource.last_name}`,
                  first_name: resource.first_name,
                  last_name: resource.last_name,
                  avatar_url: resource.avatar_url,
                  ratings: []
                });
              }

              const resourceData = resourceFeedbackMap.get(resourceId)!;
              resourceData.ratings.push(feedbackAverage);
            }
          });
        }
      }
    });

    // Calculate resource averages
    const resources: ResourceFeedbackData[] = Array.from(resourceFeedbackMap.values()).map(resourceData => {
      const overallAverage = resourceData.ratings.reduce((sum, rating) => sum + rating, 0) / resourceData.ratings.length;

      return {
        resource_id: resourceData.resource_id,
        resource_name: resourceData.resource_name,
        first_name: resourceData.first_name,
        last_name: resourceData.last_name,
        avatar_url: resourceData.avatar_url,
        average_quality_rating: overallAverage, // Use overall average for backwards compatibility
        average_experience_rating: overallAverage, // Use overall average for backwards compatibility
        overall_average_rating: Number(overallAverage.toFixed(1)),
        total_feedback_count: resourceData.ratings.length
      };
    });

    // Sort resources by overall average rating (highest first)
    resources.sort((a, b) => b.overall_average_rating - a.overall_average_rating);

    return {
      overall_business_rating: Number(overallBusinessRating.toFixed(1)),
      total_feedback_count: validFeedback.length,
      resources
    };

  } catch (error) {
    console.error('Error generating dynamic business feedback report:', error);
    return null;
  }
}

/**
 * Get feedback response rate for a business
 */
export async function getFeedbackResponseRate(
  businessId: string,
  dateRange?: { startDate?: string; endDate?: string }
): Promise<{ responseRate: number; completedBookings: number; feedbackSubmissions: number } | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // Build query for completed bookings with optional date filtering
    let completedBookingsQuery = supabase
      .from('bookings')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .eq('status', 'completed');

    // Apply date filtering to completed bookings if provided
    if (dateRange?.startDate) {
      completedBookingsQuery = completedBookingsQuery.gte('end_date', dateRange.startDate.split('T')[0]);
    }
    if (dateRange?.endDate) {
      completedBookingsQuery = completedBookingsQuery.lte('end_date', dateRange.endDate.split('T')[0]);
    }

    // Build query for feedback submissions with optional date filtering
    let feedbackQuery = supabase
      .from('booking_feedback')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .eq('status', 'submitted');

    // Apply date filtering to feedback submissions if provided
    if (dateRange?.startDate) {
      feedbackQuery = feedbackQuery.gte('submitted_at', dateRange.startDate);
    }
    if (dateRange?.endDate) {
      feedbackQuery = feedbackQuery.lte('submitted_at', dateRange.endDate);
    }

    // Execute both queries in parallel
    const [completedBookingsResult, feedbackResult] = await Promise.all([
      completedBookingsQuery,
      feedbackQuery
    ]);

    if (completedBookingsResult.error) {
      console.error('Error fetching completed bookings count:', completedBookingsResult.error);
      return null;
    }

    if (feedbackResult.error) {
      console.error('Error fetching feedback submissions count:', feedbackResult.error);
      return null;
    }

    const completedBookings = completedBookingsResult.count || 0;
    const feedbackSubmissions = feedbackResult.count || 0;

    // Calculate response rate
    const responseRate = completedBookings > 0 ? (feedbackSubmissions / completedBookings) * 100 : 0;

    console.log('Feedback response rate calculation:', {
      completedBookings,
      feedbackSubmissions,
      responseRate: responseRate.toFixed(1) + '%',
      dateRange
    });

    return {
      responseRate: Number(responseRate.toFixed(1)),
      completedBookings,
      feedbackSubmissions
    };

  } catch (error) {
    console.error('Error calculating feedback response rate:', error);
    return null;
  }
} 

/**
 * Update feedback ratings for a booking
 */
export async function updateFeedbackRatings(
  feedbackId: string,
  updates: {
    overall_quality_rating?: number;
    overall_experience_rating?: number;
    form_responses?: { [fieldKey: string]: string | number };
  }
): Promise<BookingFeedback | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    // Validate ratings are between 1-5
    if (updates.overall_quality_rating && (updates.overall_quality_rating < 1 || updates.overall_quality_rating > 5)) {
      throw new Error('Quality rating must be between 1 and 5');
    }
    if (updates.overall_experience_rating && (updates.overall_experience_rating < 1 || updates.overall_experience_rating > 5)) {
      throw new Error('Experience rating must be between 1 and 5');
    }

    // Validate form_responses ratings
    if (updates.form_responses) {
      for (const [key, value] of Object.entries(updates.form_responses)) {
        if (typeof value === 'number' && (value < 1 || value > 5)) {
          throw new Error(`Rating for ${key} must be between 1 and 5`);
        }
      }
    }

    const { data, error } = await supabase
      .from('booking_feedback')
      .update(updates)
      .eq('id', feedbackId)
      .select('*')
      .single();

    if (error) {
      console.error('Error updating feedback ratings:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error updating feedback ratings:', error);
    throw error;
  }
} 